/**
 * SharedCalculationPreview Component
 *
 * Preview page for shared calculations - shown to visitors from social media.
 * Displays a summary of the calculation results with CTAs to:
 * 1. See full details (redirect to calculator)
 * 2. Check their own tariff (start new calculation)
 */

import { Container, Stack, Paper, Title, Text, Button, Group, Badge, Divider } from '@mantine/core';
import { IconArrowRight, IconBolt, IconShare, IconEye } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { ResultSummaryCard } from './ResultSummaryCard';
import { SimpleTariffBars } from './SimpleTariffBars';
import type { CalculationResults, TariffType, TariffResult } from '../../../../types/calculator';

interface SharedCalculationPreviewProps {
  results: CalculationResults;
  currentTariff: TariffType;
  onViewFullDetails: () => void;
}

export function SharedCalculationPreview({
  results,
  currentTariff,
  onViewFullDetails
}: SharedCalculationPreviewProps) {
  // Get all tariff results and prepare data for display
  const tariffResults: { name: string; cost: number; isBest: boolean; isBaseline: boolean }[] = [];

  // Multi-OSD Support: Includes G12n (PGE) and G12r (Energa)
  const tariffKeys: (keyof CalculationResults)[] = ['g11', 'g12', 'g12as', 'g12w', 'g12n', 'g12r', 'g13', 'g13s'];

  tariffKeys.forEach(key => {
    const result = results[key] as TariffResult | undefined;
    if (result) {
      tariffResults.push({
        name: result.tariffName,
        cost: result.totalAnnualCost,
        isBest: result.isRecommended,
        isBaseline: result.tariffName === currentTariff
      });
    }
  });

  // Get recommended tariff details
  const recommendedResult = tariffResults.find(t => t.isBest);
  const currentResult = tariffResults.find(t => t.isBaseline);

  const recommendedTariff = results.recommendedTariff;
  const currentCost = currentResult?.cost || 0;
  const recommendedCost = recommendedResult?.cost || 0;
  const savings = results.potentialSavings;
  const savingsPercent = results.savingsPercent;

  // Get top 4 tariffs for simplified display
  const topTariffs = [...tariffResults]
    .sort((a, b) => a.cost - b.cost)
    .slice(0, 4);

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Paper p="lg" radius="md" bg="ecoGreen.0">
          <Group gap="xs" mb="xs">
            <IconShare size={20} color="var(--mantine-color-ecoGreen-6)" />
            <Badge color="ecoGreen" variant="light">Udostępniona kalkulacja</Badge>
          </Group>
          <Title order={2}>Ktoś poprosił Cię o opinię</Title>
          <Text c="dimmed">
            Zobacz wynik analizy taryf i pomóż podjąć decyzję o zmianie taryfy energetycznej.
          </Text>
        </Paper>

        {/* Main Result Summary */}
        <ResultSummaryCard
          recommendedTariff={recommendedTariff}
          currentTariff={currentTariff}
          currentCost={currentCost}
          recommendedCost={recommendedCost}
          savings={savings}
          savingsPercent={savingsPercent}
        />

        {/* Tariff Comparison Bars */}
        <Paper p="lg" radius="md" withBorder>
          <Title order={4} mb="md">Porównanie kosztów rocznych</Title>
          <SimpleTariffBars tariffs={topTariffs} />

          {tariffResults.length > 4 && (
            <Text size="xs" c="dimmed" mt="md" ta="center">
              + {tariffResults.length - 4} więcej taryf w pełnym porównaniu
            </Text>
          )}
        </Paper>

        <Divider />

        {/* CTAs */}
        <Stack gap="md">
          {/* Secondary CTA - View full details */}
          <Button
            variant="light"
            size="lg"
            rightSection={<IconEye size={18} />}
            onClick={onViewFullDetails}
            fullWidth
          >
            Zobacz pełne szczegóły tej kalkulacji
          </Button>

          {/* Primary CTA - Check own tariff */}
          <Button
            component={Link}
            to="/kalkulatory/podstawowy"
            size="xl"
            rightSection={<IconArrowRight size={20} />}
            leftSection={<IconBolt size={20} />}
            fullWidth
          >
            Sprawdź SWOJĄ taryfę - za darmo
          </Button>

          <Text size="xs" c="dimmed" ta="center">
            Bez rejestracji • Wynik w 30 sekund • 100% darmowe
          </Text>
        </Stack>

        {/* Info about the platform */}
        <Paper p="md" radius="md" bg="gray.0">
          <Text size="sm" c="dimmed" ta="center">
            <strong>TaryfyPradu.pl</strong> - darmowa porównywarka taryf energetycznych.
            Sprawdź, czy możesz oszczędzić na rachunkach za prąd.
          </Text>
        </Paper>
      </Stack>
    </Container>
  );
}
